import React, { useEffect, useRef } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import { useGameStore, TowerType } from '../store/gameStore';

const TouchControls: React.FC = () => {
  const { isMobile, isTablet, scaleFactor, isLandscape } = useResponsive();
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const lastPinchDistanceRef = useRef<number | null>(null);
  
  const {
    resources,
    wave,
    lives,
    isPlaying,
    isPaused,
    startGame,
    pauseGame,
    resumeGame,
    nextWave,
    selectedTowerType,
    selectTowerType
  } = useGameStore();

  // Handle touch camera controls
  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        // Single touch - start camera movement
        touchStartRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          time: Date.now()
        };
      } else if (e.touches.length === 2) {
        // Two touches - start pinch to zoom
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        lastPinchDistanceRef.current = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 1 && touchStartRef.current) {
        // Single touch - move camera
        const touch = e.touches[0];
        const deltaX = touch.clientX - touchStartRef.current.x;
        const deltaY = touch.clientY - touchStartRef.current.y;

        // Dispatch custom event for camera movement
        const event = new CustomEvent('cameraMove', {
          detail: { deltaX, deltaY }
        });
        window.dispatchEvent(event);

        touchStartRef.current = {
          x: touch.clientX,
          y: touch.clientY,
          time: Date.now()
        };
      } else if (e.touches.length === 2 && lastPinchDistanceRef.current) {
        // Two touches - pinch to zoom
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const currentDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );

        const delta = currentDistance - lastPinchDistanceRef.current;
        
        // Dispatch custom event for camera zoom
        const event = new CustomEvent('cameraZoom', {
          detail: { delta }
        });
        window.dispatchEvent(event);

        lastPinchDistanceRef.current = currentDistance;
      }
    };

    const handleTouchEnd = () => {
      touchStartRef.current = null;
      lastPinchDistanceRef.current = null;
    };

    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);
  
  // Only show touch controls on mobile or tablet
  if (!isMobile && !isTablet) return null;
  
  const buttonStyle: React.CSSProperties = {
    padding: isMobile ? '8px 12px' : '10px 14px',
    margin: '0 4px',
    backgroundColor: '#4a90e0',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: isMobile ? '12px' : '14px',
    fontWeight: '600',
    pointerEvents: 'auto',
    cursor: 'pointer',
    minWidth: isMobile ? '70px' : '80px',
    minHeight: isMobile ? '36px' : '40px',
    textAlign: 'center' as 'center',
    transform: `scale(${scaleFactor})`,
    transformOrigin: 'center',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    touchAction: 'none',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent'
  };
  
  const controlsContainerStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: isLandscape ? '5px' : '10px',
    left: 0,
    width: '100%',
    display: 'flex',
    flexDirection: isLandscape ? 'row' : 'column',
    alignItems: 'center',
    gap: isLandscape ? '4px' : '8px',
    padding: isLandscape ? '8px' : '12px',
    zIndex: 100,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    backdropFilter: 'blur(10px)',
    transform: `scale(${scaleFactor})`,
    transformOrigin: 'bottom center',
    touchAction: 'none'
  };
  
  const gameInfoStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    width: isLandscape ? 'auto' : '100%',
    maxWidth: isLandscape ? '200px' : '300px',
    padding: isLandscape ? '6px 10px' : '10px 12px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: '8px',
    color: 'white',
    marginBottom: isLandscape ? '0' : '4px',
    marginRight: isLandscape ? '8px' : '0',
    fontWeight: '600',
    fontSize: isMobile ? '12px' : '14px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    touchAction: 'none'
  };

  const towerButtonStyle = (type: TowerType): React.CSSProperties => ({
    ...buttonStyle,
    backgroundColor: selectedTowerType === type ? '#4ae04a' : '#4a90e0',
    minWidth: isMobile ? '60px' : '70px',
    minHeight: isMobile ? '32px' : '36px',
    fontSize: isMobile ? '11px' : '13px',
    padding: isMobile ? '6px 10px' : '8px 12px'
  });

  const gameControlsStyle: React.CSSProperties = {
    display: 'flex',
    marginBottom: isLandscape ? '0' : '8px',
    marginRight: isLandscape ? '8px' : '0',
    gap: '8px',
    touchAction: 'none'
  };

  const towerSelectionStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '6px',
    width: isLandscape ? 'auto' : '100%',
    maxWidth: isLandscape ? '200px' : '300px',
    touchAction: 'none'
  };

  const gestureHintsStyle: React.CSSProperties = {
    color: 'white',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    marginTop: isLandscape ? '0' : '6px',
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    touchAction: 'none'
  };

  return (
    <div style={controlsContainerStyle}>
      {/* Game info */}
      <div style={gameInfoStyle}>
        <div>💰 {resources}</div>
        <div>🌊 {wave}</div>
        <div>❤️ {lives}</div>
      </div>
      
      {/* Game controls */}
      <div style={gameControlsStyle}>
        {!isPlaying ? (
          <button 
            style={{...buttonStyle, backgroundColor: '#4ae04a'}} 
            onClick={startGame}
          >
            ▶️ Start
          </button>
        ) : isPaused ? (
          <button 
            style={{...buttonStyle, backgroundColor: '#4ae04a'}} 
            onClick={resumeGame}
          >
            ▶️ Resume
          </button>
        ) : (
          <button 
            style={buttonStyle} 
            onClick={pauseGame}
          >
            ⏸️ Pause
          </button>
        )}
        
        {isPlaying && !isPaused && (
          <button 
            style={{...buttonStyle, backgroundColor: '#e04a4a'}} 
            onClick={nextWave}
          >
            ⚡ Next Wave
          </button>
        )}
      </div>
      
      {/* Tower selection */}
      <div style={towerSelectionStyle}>
        <button 
          style={towerButtonStyle('science')}
          onClick={() => selectTowerType(selectedTowerType === 'science' ? null : 'science')}
        >
          🔬 Science
        </button>
        
        <button 
          style={towerButtonStyle('math')}
          onClick={() => selectTowerType(selectedTowerType === 'math' ? null : 'math')}
        >
          📐 Math
        </button>
        
        <button 
          style={towerButtonStyle('art')}
          onClick={() => selectTowerType(selectedTowerType === 'art' ? null : 'art')}
        >
          🎨 Art
        </button>
        
        <button 
          style={towerButtonStyle('drama')}
          onClick={() => selectTowerType(selectedTowerType === 'drama' ? null : 'drama')}
        >
          🎭 Drama
        </button>
      </div>
      
      {/* Gesture hints */}
      <div style={gestureHintsStyle}>
        <span>👆 Drag to move</span>
        <span>🤏 Pinch to zoom</span>
        <span>👆 Tap to place</span>
      </div>
    </div>
  );
};

export default TouchControls; 